import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Game } from '../srm/srm-parser'
import { setPath, wordOf } from '../utils'
import BossesSection from './BossesSection'
import CollectionSection from './CollectionSection'
import EquipmentSection from './EquipmentSection'
import ResourcesSection from './ResourcesSection'
import TimeSection from './TimeSection'

export interface GameCardHandle {
  getGame: () => Game
}

const GameCard = forwardRef<GameCardHandle, { label: string; game: Game }>(
  ({ label, game }, ref) => {
    const [g, setG] = useState<Game>(game)
    const gRef = useRef(g)
    gRef.current = g

    useEffect(() => {
      setG(game)
    }, [game])

    useImperativeHandle(ref, () => ({ getGame: () => gRef.current }))

    const updateResource = (
      resource: 'energy' | 'reserve' | 'missiles' | 'superMissiles' | 'powerBombs',
      field: 'current' | 'max',
      value: number
    ) =>
      setG((prev) => ({
        ...prev,
        resources: {
          ...prev.resources,
          [resource]: { ...prev.resources[resource], [field]: wordOf(value) }
        }
      }))

    const updateTime = (field: 'hours' | 'minutes', value: number) =>
      setG((prev) => ({
        ...prev,
        time: { ...prev.time, [field]: wordOf(value) }
      }))

    const updateEquipment = (
      path: string[],
      bit: 'collected' | 'inInventory' | 'equipped',
      value: boolean
    ) =>
      setG((prev) => ({
        ...prev,
        equipment: setPath(prev.equipment, [...path, bit], value)
      }))

    const updateBoss = (key: keyof Game['bosses'], value: boolean) =>
      setG((prev) => ({ ...prev, bosses: { ...prev.bosses, [key]: value } }))

    const updateCollectionArray = (
      area: keyof Game['collectionItems'],
      field: string,
      index: number,
      value: boolean
    ) =>
      setG((prev) => {
        const areaData = { ...(prev.collectionItems[area] as any) }
        const arr = [...areaData[field]]
        arr[index] = value
        areaData[field] = arr
        return { ...prev, collectionItems: { ...prev.collectionItems, [area]: areaData } }
      })

    const updateCollectionBool = (
      area: keyof Game['collectionItems'],
      field: string,
      value: boolean
    ) =>
      setG((prev) => ({
        ...prev,
        collectionItems: {
          ...prev.collectionItems,
          [area]: { ...(prev.collectionItems[area] as any), [field]: value }
        }
      }))

    return (
      <div className="game-card">
        <h2 className="game-card-title">{label}</h2>
        <TimeSection time={g.time} onUpdate={updateTime} />
        <ResourcesSection resources={g.resources} onUpdate={updateResource} />
        <EquipmentSection equipment={g.equipment} onUpdate={updateEquipment} />
        <BossesSection bosses={g.bosses} onUpdate={updateBoss} />
        <CollectionSection
          collectionItems={g.collectionItems}
          onUpdateArray={updateCollectionArray}
          onUpdateBool={updateCollectionBool}
        />
      </div>
    )
  }
)

export default GameCard
